// src/features/dashboard/teacher/ClassManagement.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../../hooks/useAuth';
import {
  fetchClassStudents,
  bulkAddStudents,
  updateStudent,
  updateClassForAllStudents
} from '../../../services/sheetDataService';
import AttendanceRegister from './AttendanceRegister';
import MarksEntry from './MarksEntry';
import AddStudentsSection from './AddStudentsSection';
import ClassSchedule from './ClassSchedule';
import ImportStudentsModal from './ImportStudentsModal';
import EditClassModal from './EditClassModal';
import TopicsManager from './TopicsManager';

const ClassManagement = ({ classItem, onClassUpdated }) => {
  const { accessToken, user } = useAuth();
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('students');
  const [showAddSection, setShowAddSection] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  // Memoized helpers for domain‑specific spreadsheet IDs
  const getUsersSheetId = useCallback(() => user.usersSpreadsheetId || user.spreadsheetId, [user]);
  const getAttendanceSheetId = useCallback(() => user.attendanceSpreadsheetId || user.spreadsheetId, [user]);
  const getMarksSheetId = useCallback(() => user.marksSpreadsheetId || user.spreadsheetId, [user]);
  const getTopicsSheetId = useCallback(() => user.topicsSpreadsheetId || user.spreadsheetId, [user]);

  // Load students
  const loadStudents = useCallback(async () => {
    setLoading(true);
    try {
      const usersSheetId = getUsersSheetId();
      console.log('📥 Loading students for classItem:', classItem);
      const data = await fetchClassStudents(accessToken, usersSheetId, classItem);
      console.log('✅ Students loaded:', data.length, data);
      setStudents(data);
    } catch (err) {
      console.error('❌ Error loading students:', err);
    } finally {
      setLoading(false);
    }
  }, [accessToken, classItem, getUsersSheetId]);

  useEffect(() => {
    loadStudents();
  }, [loadStudents]);

  // Add students
  const handleAddStudents = async (newStudents) => {
    const metadata = {
      organisationId: user.organisationId || 'N/A',
      school: user.school || 'N/A',
      province: user.province || 'N/A',
      district: user.district || 'N/A',
      circuit: user.circuit || 'N/A',
      teacher: user.email,
      subject: classItem.subject,
      grade: classItem.grade,
      googleClassroomLink: classItem.googleClassroomLink || '',
      schedule: classItem.schedule || [],
      zoomLink: classItem.zoomLink || '',
      zoomPassword: classItem.zoomPassword || '',
    };

    try {
      const usersSheetId = getUsersSheetId();
      const result = await bulkAddStudents(accessToken, usersSheetId, newStudents, metadata);
      await loadStudents();
      setShowAddSection(false);
      alert(`✅ Successfully added ${result.added} student(s)!`);
    } catch (err) {
      alert('❌ ' + (err.message || 'Failed to add students.'));
    }
  };

  // Edit a single student
  const handleEditStudent = async (rowNumber, name, surname, email, userDirRowNumber) => {
    try {
      const usersSheetId = getUsersSheetId();
      const attendanceSheetId = getAttendanceSheetId();
      const marksSheetId = getMarksSheetId();

      await updateStudent(
        accessToken,
        usersSheetId,
        rowNumber,
        name,
        surname,
        email,
        userDirRowNumber,
        attendanceSheetId,
        marksSheetId
      );
      await loadStudents();

    } catch (err) {
      alert('Failed to update student: ' + err.message);
    }
  };

  // Edit class‑wide schedule / links
  /*const handleSaveClassEdits = async (updatedData) => {
    try {
      const usersSheetId = getUsersSheetId();
      const result = await updateClassForAllStudents(accessToken, usersSheetId, classItem, updatedData);

      if (result.customCount && result.customCount > 0) {
        const confirmMessage = `${result.customCount} student(s) have custom schedules. 
Updating the class will overwrite them. ${result.defaultCount} student(s) use the default schedule and will be updated normally.

Do you want to proceed and overwrite custom schedules?`;
        if (!window.confirm(confirmMessage)) return;

        const forceResult = await updateClassForAllStudents(
          accessToken,
          usersSheetId,
          classItem,
          updatedData,
          true
        );
        if (forceResult.updated === 0) {
          alert('No students were updated.');
          return;
        }
        if (onClassUpdated) await onClassUpdated();
        await loadStudents();
        alert(`✅ Class updated successfully! (${forceResult.updated} students updated)`);
        return;
      }

      if (result.updated === 0) {
        alert(result.message || 'No students were updated.');
        return;
      }
      if (onClassUpdated) await onClassUpdated();
      await loadStudents();
      alert(`✅ Class updated successfully! (${result.updated} students updated)`);
    } catch (err) {
      alert('❌ Failed to update class: ' + err.message);
    }
  };*/


  // Edit class‑wide schedule / links
  const handleSaveClassEdits = async (updatedData) => {
    try {
      const usersSheetId = getUsersSheetId();
      // ✅ Pass the full updatedData (including termData)
      const result = await updateClassForAllStudents(accessToken, usersSheetId, classItem, updatedData);

      if (result.customCount && result.customCount > 0) {
        const confirmMessage = `${result.customCount} student(s) have custom schedules. 
Updating the class will overwrite them. ${result.defaultCount} student(s) use the default schedule and will be updated normally.

Do you want to proceed and overwrite custom schedules?`;
        if (!window.confirm(confirmMessage)) return;

        const forceResult = await updateClassForAllStudents(
          accessToken,
          usersSheetId,
          classItem,
          updatedData,
          true
        );
        if (forceResult.updated === 0) {
          alert('No students were updated.');
          return;
        }
        if (onClassUpdated) await onClassUpdated();
        await loadStudents();
        alert(`✅ Class updated successfully! (${forceResult.updated} students updated)`);
        return;
      }

      if (result.updated === 0) {
        alert(result.message || 'No students were updated.');
        return;
      }
      if (onClassUpdated) await onClassUpdated();
      await loadStudents();
      alert(`✅ Class updated successfully! (${result.updated} students updated)`);
    } catch (err) {
      alert('❌ Failed to update class: ' + err.message);
    }
  };

  if (loading) return <div className="text-center py-4">Loading students...</div>;

  return (
    <div className="bg-white rounded shadow p-4">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-2xl font-bold">{classItem.subject} - {classItem.grade}</h2>
        <div className="flex gap-2">
          <button
            onClick={() => setShowEditModal(true)}
            className="px-3 py-1 rounded bg-yellow-600 hover:bg-yellow-700 text-white"
          >
            ✏️ Edit Class
          </button>
          <button
            onClick={() => setShowImportModal(true)}
            className="px-3 py-1 rounded bg-purple-600 hover:bg-purple-700 text-white"
          >
            📥 Import Students
          </button>
          <button
            onClick={() => setShowAddSection(!showAddSection)}
            className={`px-3 py-1 rounded ${showAddSection ? 'bg-gray-500 hover:bg-gray-600' : 'bg-green-600 hover:bg-green-700'
              } text-white`}
          >
            {showAddSection ? 'Cancel' : '+ Add Students'}
          </button>
        </div>
      </div>

      {showAddSection && (
        <AddStudentsSection
          classId={classItem.rowNumber}
          subject={classItem.subject}
          onStudentsAdded={handleAddStudents}
          onCancel={() => setShowAddSection(false)}
          teacherName={`${user.name} ${user.surname}`}
          teacherEmail={user.email}
          organisationId={user.organisationId}
          school={user.school}
          province={user.province}
          district={user.district}
          circuit={user.circuit}
        />
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-4">
        <nav className="flex space-x-4">
          {['students', 'attendance', 'marks', 'schedule', 'topics'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`py-2 px-3 capitalize ${activeTab === tab ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500'
                }`}
            >
              {tab}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab content */}
      {activeTab === 'students' && (
        <div>
          <table className="w-full table-auto border-collapse">
            <thead>
              <tr className="bg-gray-100">
                <th className="px-4 py-2 text-left">Name</th>
                <th className="px-4 py-2 text-left">Surname</th>
                <th className="px-4 py-2 text-left">Email</th>
                <th className="px-4 py-2 text-left">Grade</th>
                <th className="px-4 py-2 text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {students.map(s => (
                <tr key={s.userId} className="border-t">
                  <td className="px-4 py-2">{s.name}</td>
                  <td className="px-4 py-2">{s.surname}</td>
                  <td className="px-4 py-2">{s.email}</td>
                  <td className="px-4 py-2">{s.grade}</td>
                  <td className="px-4 py-2">
                    <button
                      onClick={() => {
                        const newName = prompt('New name:', s.name);
                        if (newName) {
                          const newSurname = prompt('New surname:', s.surname);
                          if (newSurname) {
                            const newEmail = prompt('New email:', s.email);
                            if (newEmail) {
                              handleEditStudent(s.rowNumber, newName, newSurname, newEmail, s.userDirRowNumber);
                            }
                          }
                        }
                      }}
                      className="text-blue-600 hover:underline"
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {activeTab === 'attendance' && (
        <AttendanceRegister
          classItem={classItem}
          students={students}
          spreadsheetId={getAttendanceSheetId()}
        />
      )}
      {activeTab === 'marks' && (
        <MarksEntry
          classItem={classItem}
          students={students}
          spreadsheetId={getMarksSheetId()}
        />
      )}
      {activeTab === 'schedule' && (
        <ClassSchedule classItem={classItem} />
      )}
      {activeTab === 'topics' && (
        <TopicsManager
          classItem={classItem}
          spreadsheetId={getTopicsSheetId()}
        />
      )}

      {/* Modals */}
      <EditClassModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        classItem={classItem}
        onSave={handleSaveClassEdits}
      />

      <ImportStudentsModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        currentClass={classItem}
        onImportComplete={loadStudents}
      />
    </div>
  );
};

export default ClassManagement;